import {
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library.base-ui.theme";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Flex } from "@caspeco/casper-ui-library.components.flex";
import { Heading } from "@caspeco/casper-ui-library.components.heading";
import { Image } from "@caspeco/casper-ui-library.components.image";
import { FC, PropsWithChildren, useEffect, useState } from "react";
import { CSSVarHeaderHeight } from "style-variables";
import { HamburgerButton } from "./hamburger-button";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { IconButton } from "@caspeco/casper-ui-library.components.icon-button";
import { Icons } from "@caspeco/casper-ui-library.components.icon";
import { closeBookingModal } from "utils/modal-utils";
import { useTranslation } from "react-i18next";
import { useDisclosure } from "@caspeco/casper-ui-library.components.modal";
import ConfirmationDialog from "components/confirm-dialog";
import { isGuid } from "utils";

interface IHeaderProps {
    shouldHideCoverImage: boolean;
    hasCoverImage: boolean;
    isModal: boolean;
}

const Header = ({ shouldHideCoverImage, hasCoverImage, isModal }: IHeaderProps) => {
    const { isLoading, error, data } = useExternalBookingSettings();
    const [hasScrolled, setHasScrolled] = useState(false);
    const { t } = useTranslation();

    const hasGuid = () => {
        const locationPathnames = location.pathname.split("/");
        return locationPathnames.some((path) => isGuid(path));
    };

    function onScroll() {
        const newValue = window.scrollY > 0;
        setHasScrolled(newValue);
    }
    useEffect(() => {
        document.addEventListener("scroll", onScroll);
        return () => {
            document.removeEventListener("scroll", onScroll);
        };
    }, [hasScrolled]);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const getHeaderBGColor = () => {
        let bgColor = "none";

        if (shouldHideCoverImage || hasScrolled || !hasCoverImage) {
            bgColor = "var(--theme-colors-primary-wlv)";
        }

        return bgColor;
    };

    /**
     * If the user made any choices we prompt the user
     */
    const closeApplicationModal = () => {
        const currentParams = new URLSearchParams(window.location.search);
        const hasGuests = currentParams.get("guests") !== null;

        if (hasGuests || hasGuid()) {
            onOpen();
        } else {
            closeBookingModal();
        }
    };

    const ConditionalHeader: FC<PropsWithChildren<{ condition: boolean }>> = ({
        condition,
        children,
    }) => {
        if (condition) {
            return (
                <Heading
                    as="h1"
                    color={ThemeColorVariable.OnPrimary}
                    fontSize={{
                        base: ThemeFontSizeVariable.XLarge,
                        md: ThemeFontSizeVariable.X2Large,
                    }}
                    fontFamily={ThemeFontVariable.Header}
                    fontWeight="bold"
                    textAlign="center"
                    noOfLines={2}
                >
                    {children}
                </Heading>
            );
        }
        return <>{children}</>;
    };

    return !isLoading && data ? (
        <Box
            display="flex"
            as="header"
            position="fixed"
            top="0"
            width="100%"
            zIndex="3"
            h={CSSVarHeaderHeight}
            p={ThemeSpaceVariable.Medium}
            justifyContent="space-between"
            alignItems="center"
            backgroundColor={getHeaderBGColor()}
            // @ts-expect-error // ! TODO: Include transition in ThemeStyleProps for box in casper-ui-library
            transition={!shouldHideCoverImage ? "background-color 0.2s ease-in-out" : ""}
        >
            {!isModal && (
                <Box display="flex" flex={1} justifyContent="flex-start">
                    {!error && <HamburgerButton />}
                </Box>
            )}
            <Flex justifyContent="center" flex={3}>
                <ConditionalHeader condition={!isModal}>
                    {data.themeSettings?.logo.imageUrl ? (
                        <Image
                            src={data.themeSettings.logo.imageUrl}
                            zIndex="2"
                            maxHeight={CSSVarHeaderHeight}
                            alt={data.unitsMetaData.name}
                        />
                    ) : (
                        data.unitsMetaData.name
                    )}
                </ConditionalHeader>
            </Flex>
            {isModal ? (
                <Box
                    tabIndex={0}
                    position="absolute"
                    zIndex={2}
                    right={0}
                    p={ThemeSpaceVariable.Medium}
                >
                    <IconButton
                        aria-label={t("application.closeBookingModal")}
                        size={["md", "md", "lg"]}
                        icon={Icons.Close}
                        onClick={closeApplicationModal}
                        tabIndex={0}
                    />
                </Box>
            ) : (
                <Flex flex="1" />
            )}
            <ConfirmationDialog
                primaryButtonText={t("exitWarningDialog.exitButton")}
                secondaryButtonText={t("exitWarningDialog.stayButton")}
                isOpen={isOpen}
                title={t("exitWarningDialog.title")}
                message={t("exitWarningDialog.message")}
                onClose={onClose}
                secondaryAction={onClose}
                primaryAction={closeBookingModal}
            />
        </Box>
    ) : null;
};

export default Header;
